import type { FC } from "react";

import { useFormContext } from "@ui/components/Form";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { UiWarningIcon } from "@jti/brand-icons/ui-warning";

import { useSavBlocking } from "../../SavBlockingContext";

import { BirthDateDropdowns } from "./components/BirthDateDropdowns/BirthDateDropdowns";
import { BirthDateInputs } from "./components/BirthDateInputs/BirthDateInputs";
import { RegionSelector } from "./components/RegionSelector";

import styles from "./BirthDateFormInputs.module.scss";

const { sanitize } = dompurify;

export type BirthFormDefaultProps = {
  minimumAge?: number;
  validationMessage?: string;
  dayPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  dayAriaLabel?: string;
  monthAriaLabel?: string;
  yearAriaLabel?: string;
  headline?: string;
  description?: string;
  isInputsHidden?: boolean;
  regionSelectorTitle?: string;
};

export type BirthDateFormDropdownsFormat = "dd/mm/yyyy" | "mm/yyyy" | "yyyy";
export type BirthDateFormInputsFormat = "dd/mm/yyyy" | "yyyy/mm/dd" | "yyyy";

export type BirthFormInputsProps = {
  inputType: "inputs";
  format: BirthDateFormInputsFormat;
} & BirthFormDefaultProps;

export type BirthFormDropdownsProps = {
  inputType: "dropdowns";
  format: BirthDateFormDropdownsFormat;
} & BirthFormDefaultProps;

export type BirthDateFormInputsProps = BirthFormInputsProps | BirthFormDropdownsProps;

export type DateInputProps = {
  placeholder: string;
  ariaLabel: string;
  type: "textInput" | "select";
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

type MinimumAgeConfig = {
  year: number;
  month: number;
  day: number;
  minimumAge: number;
};

export const isMinimumAgeReached = ({ year, month, day, minimumAge }: MinimumAgeConfig) => {
  const date = new Date(year + minimumAge, month - 1, day);
  return date.getTime() <= new Date().getTime();
};

export const BirthDateFormInputs: FC<BirthDateFormInputsProps> = ({
  minimumAge = 18,
  validationMessage,
  dayPlaceholder,
  monthPlaceholder,
  yearPlaceholder,
  dayAriaLabel,
  monthAriaLabel,
  yearAriaLabel,
  inputType,
  headline,
  description,
  isInputsHidden,
  regionSelectorTitle,
  format,
}) => {
  const {
    formState: { errors },
  } = useFormContext();
  const { isBlocked, feedbackMessage } = useSavBlocking();
  const hasValidationErrors = errors.year || errors.month || errors.day;

  if (isBlocked) {
    return (
      <div className={styles.container}>
        <div
          className={styles.content}
          data-testid="sav-blocking-message"
          dangerouslySetInnerHTML={{ __html: sanitize(feedbackMessage ?? "") }}
        ></div>
      </div>
    );
  }

  const resolvedDayAriaLabel = dayAriaLabel || dayPlaceholder || "";
  const resolvedMonthAriaLabel = monthAriaLabel || monthPlaceholder || "";
  const resolvedYearAriaLabel = yearAriaLabel || yearPlaceholder || "";

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.headline}>{headline}</h2>
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: sanitize(description ?? "") }}></div>
      </div>
      {!isInputsHidden && (
        <div className={styles.inputsContainer}>
          <div className={classNames(styles.inputs, { [styles.inputsWithGap]: inputType === "inputs" })}>
            {inputType === "inputs" && (
              <BirthDateInputs
                format={format}
                dayPlaceholder={dayPlaceholder ?? ""}
                monthPlaceholder={monthPlaceholder ?? ""}
                yearPlaceholder={yearPlaceholder ?? ""}
                dayAriaLabel={resolvedDayAriaLabel}
                monthAriaLabel={resolvedMonthAriaLabel}
                yearAriaLabel={resolvedYearAriaLabel}
                minimumAge={minimumAge}
              />
            )}
            {inputType === "dropdowns" && (
              <BirthDateDropdowns
                dayPlaceholder={dayPlaceholder ?? ""}
                monthPlaceholder={monthPlaceholder ?? ""}
                yearPlaceholder={yearPlaceholder ?? ""}
                dayAriaLabel={resolvedDayAriaLabel}
                monthAriaLabel={resolvedMonthAriaLabel}
                yearAriaLabel={resolvedYearAriaLabel}
                format={format}
                minimumAge={minimumAge}
              />
            )}
          </div>
          {hasValidationErrors && (
            <div className={styles.errorMessage}>
              <UiWarningIcon className={styles.errorIcon} />
              <span>{validationMessage}</span>
            </div>
          )}
        </div>
      )}
      <RegionSelector title={regionSelectorTitle} />
    </div>
  );
};
